// Sidebar.jsx
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiBarChart2, FiHome, FiBell, FiSettings, FiLayers, FiCheckSquare } from "react-icons/fi";
import { useMode } from "../contexts/ModeContext";

export default function Sidebar({ open = false, onClose }) {
  const { pathname = "/" } = useLocation();
  const { mode, setMode } = useMode();

  const isActive = (path) => {
    // for root routes, match exactly (ignore deeper paths so Home isn't always active)
    if (path === "/websurrogate" || path === "/legacy" || path === "/violations" || path === "/violations-v2" || path === "/acris-legacy") {
      return pathname === path || pathname === `${path}/`;
    }
    if (path === "/violations-v2/dashboard") {
      return pathname === path || pathname === `${path}/`;
    }
    if (path === "/violations-v2/properties") {
      return (
        pathname === path ||
        pathname === `${path}/` ||
        (pathname.startsWith("/violations-v2/") && !pathname.startsWith("/violations-v2/dashboard"))
      );
    }
    if (path === "/acris-legacy/leads") {
      return pathname === path || pathname.startsWith("/acris-legacy/leads");
    }
    if (path === "/acris-legacy/multiple") {
      return pathname === path || pathname.startsWith("/acris-legacy/multiple");
    }
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  const baseBtn = "group relative ui-sidebar-item";
  const activeBtn = "ui-sidebar-item--active";
  const inactiveBtn = "";

  const handleNavClick = (e, path) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    if (pathname === path) {
      e.preventDefault();
      window.location.reload();
      return;
    }
    onClose?.();
  };

  const menus = {
    webSurrogate: [
      { label: "Dashboard", to: "/websurrogate/dashboard", icon: <FiBarChart2 /> },
      { label: "Home", to: "/websurrogate", icon: <FiHome /> },
      { label: "Status", to: "/websurrogate/check-case-status", icon: <FiCheckSquare /> },
      { label: "Run Report", to: "/websurrogate/report", icon: <FiLayers /> },
      { label: "Notifications", to: "/websurrogate/notifications", icon: <FiBell /> },
      { label: "Settings", to: "/websurrogate/settings", icon: <FiSettings /> },
    ],
    legacy: [
      { label: "Dashboard", to: "/legacy/dashboard", icon: <FiBarChart2 /> },
      { label: "Home", to: "/legacy", icon: <FiHome /> },
      { label: "Fresh leads", to: "/legacy/fresh-leads", icon: <FiLayers /> },
      { label: "Notifications", to: "/legacy/notifications", icon: <FiBell /> },
    ],
    violation: [
      { label: "Dashboard", to: "/violations/dashboard", icon: <FiBarChart2 /> },
      { label: "Home", to: "/violations", icon: <FiHome /> },
    ],
    violationsV2: [
      { label: "Dashboard", to: "/violations-v2/dashboard", icon: <FiBarChart2 /> },
      { label: "Properties", to: "/violations-v2/properties", icon: <FiHome /> },
    ],
    acrisLegacy: [
      { label: "Dashboard", to: "/acris-legacy/dashboard", icon: <FiBarChart2 /> },
      { label: "Probate Leads", to: "/acris-legacy/leads", icon: <FiHome /> },
      { label: "Multiple Records", to: "/acris-legacy/multiple", icon: <FiLayers /> },
    ],
  };

  useEffect(() => {
    try {
      const ls = localStorage.getItem("app_mode");
      if (!ls) {
        if (pathname.startsWith("/acris-legacy")) {
          setMode && setMode("acris-legacy");
        } else if (pathname.startsWith("/violations-v2")) {
          setMode && setMode("violations-v2");
        } else if (pathname.startsWith("/violations")) {
          setMode && setMode("violation");
        } else if (pathname.startsWith("/legacy")) {
          setMode && setMode("legacy");
        } else {
          setMode && setMode("webSurrogate");
        }
      }
    } catch (err) {
      console.warn("Sidebar: could not access localStorage", err);
    }
  }, []);

  const currentMenu =
    mode === "legacy"
      ? menus.legacy
      : mode === "violation"
        ? menus.violation
        : mode === "violations-v2"
          ? menus.violationsV2
          : mode === "acris-legacy"
            ? menus.acrisLegacy
            : menus.webSurrogate;

  return (
    <>
      {open && (
        <button
          type="button"
          onClick={onClose}
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          aria-label="Close sidebar overlay"
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-30 h-screen-full w-44 lg:w-48 text-[var(--fg)] flex flex-col p-4 transition-transform duration-200 ease-out md:translate-x-0 md:static md:z-auto ui-sidebar overflow-y-auto ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--fg-muted)]">Workspace</p>
            <h2 className="text-base font-bold text-[var(--fg-strong)]">
              {mode === "legacy"
                ? "Legacy System"
                : mode === "violation"
                  ? "Violation"
                  : mode === "violations-v2"
                    ? "Violations"
                    : mode === "acris-legacy"
                      ? "ACRIS Legacy"
                    : "Web Surrogate"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="md:hidden ui-btn ui-btn-ghost text-[11px]"
          >
            Close
          </button>
        </div>

        <nav className="space-y-1">
          {currentMenu.map((m) => {
            const to = m.to;
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                onClick={(e) => handleNavClick(e, to)}
                className={`${baseBtn} ${active ? activeBtn : inactiveBtn}`}
                aria-current={active ? "page" : undefined}
              >
                <span className="text-base">{m.icon}</span>
                <span className="flex-1 text-left leading-snug whitespace-normal">{m.label}</span>
                {active && <span className="absolute right-3 h-2 w-2 rounded-full bg-[var(--accent)]" />}
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
